/**
 * Environment Configuration Manager
 * Handles environment detection and provides environment-specific settings
 */

// Detect current environment
export const ENV = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
};

/**
 * Get the current environment
 * Priority: NEXT_PUBLIC_ENV > NODE_ENV
 */
export const getCurrentEnvironment = () => {
  const publicEnv = process.env.NEXT_PUBLIC_ENV;
  const nodeEnv = process.env.NODE_ENV;
  
  if (publicEnv) {
    return publicEnv;
  }
  
  return nodeEnv === 'production' ? ENV.PRODUCTION : ENV.DEVELOPMENT;
};

/**
 * Check if running in specific environment
 */
export const isDevelopment = () => getCurrentEnvironment() === ENV.DEVELOPMENT;
export const isStaging = () => getCurrentEnvironment() === ENV.STAGING;
export const isProduction = () => getCurrentEnvironment() === ENV.PRODUCTION;

/**
 * Get API base URL based on current environment
 */
export const getApiBaseUrl = () => {
  const env = getCurrentEnvironment();
  
  // Check if testing mode is enabled
  if (process.env.NEXT_PUBLIC_TESTING_DATA === 'true') {
    if (process.env.NEXT_PUBLIC_ENV === 'development') {
      console.log('🧪 Testing mode enabled - using mock data');
    }
    return null;
  }
  
  switch (env) {
    case ENV.STAGING:
      return process.env.NEXT_PUBLIC_STAGING_API_BASE_URL;
    case ENV.PRODUCTION:
      return process.env.NEXT_PUBLIC_PRODUCTION_API_BASE_URL;
    case ENV.DEVELOPMENT:
    default:
      return process.env.NEXT_PUBLIC_API_BASE_URL;
  }
};

/**
 * Environment-specific configuration
 */
export const config = {
  // Current environment
  env: getCurrentEnvironment(),
  
  // API configuration
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: isDevelopment() ? 30000 : 15000, // 30s dev, 15s prod/staging
  },
  
  // Logging configuration
  logging: {
    enabled: !isProduction(),
    level: isStaging() ? 'debug' : isDevelopment() ? 'info' : 'error',
    apiCalls: isDevelopment() || isStaging(),
    performance: isStaging(),
  },
  
  // Feature flags
  features: {
    debugMode: isDevelopment() || isStaging(),
    sourceMap: isStaging(),
    strictMode: !isProduction() || isStaging(),
    analytics: isProduction(),
    errorReporting: isProduction() || isStaging(),
  },
  
  // Security settings
  security: {
    tokenRefreshInterval: isProduction() ? 840000 : 600000, // 14min prod, 10min dev
    sessionTimeout: isProduction() ? 3600000 : 7200000, // 1hr prod, 2hr dev
  },
  
  // Testing
  testing: {
    useMockData: process.env.NEXT_PUBLIC_TESTING_DATA === 'true',
  },
};

/**
 * Log current environment configuration (only in development)
 * Note: Staging should also not expose this information
 */
if (typeof window !== 'undefined' && isDevelopment()) {
  console.log('🌍 Environment Configuration:', {
    environment: config.env,
    apiBaseUrl: config.api.baseUrl,
    debugMode: config.features.debugMode,
    testing: config.testing.useMockData,
  });
}

export default config;
